import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  BellOff,
  CalendarClock,
  Ban,
  RefreshCw,
  Send,
  XCircle,
  MoreHorizontal,
  StickyNote,
} from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Label,
  DatePicker,
  Textarea,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  invalidateAfterLeave,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { useBetterAuth } from '@hooks/useAuth';
import { PERMISSIONS } from '@/permissions';
import { http, ApiError } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import type { components } from '@/types/api.generated';
import type { FulfillmentJob } from '../jobResource';

/**
 * Per-job command affordances on the fulfillment-job detail view (#1268).
 *
 * The backend exposes six single-job commands (all `dispatch:write`,
 * mutating → auto-audited): schedule-manually / cancel / reschedule for a
 * `planned` job, cancel-dispatched for a `dispatched` job, and refetch when the
 * backend marks the job `refetchable`: bound to an Onfleet task and not
 * terminal, or completed with a Shopify write-back that started but recorded
 * no fulfillment id, which the refetch retries (#2807). The
 * close-writeback command is available on completed jobs marked
 * `writeback_closable`; it only sets a local marker and never writes Shopify. The
 * operator-note editor is available in every lifecycle state and remains
 * local after task creation. The whole toolbar is gated on `dispatch:write`
 * — an operator without the grant sees no buttons at all (repo forbids the
 * hidden-button-as-permission anti-pattern; the gate returns `null`).
 *
 * On success we blanket-invalidate the `dispatch/jobs` namespace
 * (`queryKeys.dispatchJobs.all()`), which is a TanStack prefix of both the
 * list (`[slug,'list',…]`) and the open detail (`[slug,'detail',id]`), so
 * the drawer / routed detail and the list behind it refresh in one sweep.
 *
 * When the result says the job is gone (cancel / cancel-dispatched, or a
 * refetch that tombstones the job; see `JOB_REMOVED`), we call the kit's
 * `leaveDetail` and refresh through `invalidateAfterLeave`: the job's detail
 * is only marked stale, the rest of the namespace refetches. The backend
 * soft-deletes every cancelled job, so a refetch of a detail still observed
 * would get a 404 and show it inline (#2793, #2802).
 */

// The 409 body for a driver-assigned Onfleet task: `commandRejectedBody`
// (backend dispatch-routes) emits `VALIDATION_FAILED` with the machine
// reason on `error.extras.reason`, surfaced on `ApiError.extras`. This is
// the one rejection with a distinct operator copy ("driver already
// accepted, can't auto-cancel") rather than the generic backend message.
const ONFLEET_ASSIGNED_REASON = 'onfleet_task_assigned';

export function isOnfleetAssignedConflict(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    (err.extras as { reason?: unknown } | undefined)?.reason === ONFLEET_ASSIGNED_REASON
  );
}

type JobCommandResult =
  | components['schemas']['FulfillmentJobCommandResult']
  | components['schemas']['RefetchOnfleetTaskResult'];

// The backend results that mean the job is gone. Every →cancelled
// transition also sets deleted_at (backend transition() in
// fulfillment-jobs-writes.ts) and the detail read filters
// deleted_at IS NULL, so the detail would 404 on its next fetch (#2793).
const JOB_REMOVED = {
  status: 'cancelled',
  refetchDecision: 'released:tombstoned',
} as const;

function removesJob(result: JobCommandResult | undefined): boolean {
  if (result === undefined) return false;
  if ('status' in result) return result.status === JOB_REMOVED.status;
  return result.decision === JOB_REMOVED.refetchDecision;
}

export function JobActionsToolbar({
  job,
  leaveDetail,
}: {
  job: FulfillmentJob;
  /** Leaves the detail surface; called when a command removes the job.
   *  Absent ⇒ the detail stays mounted and the whole namespace refetches,
   *  as `useRowActionRunner` does. */
  leaveDetail?: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useBetterAuth();
  const toast = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();

  const [pending, setPending] = useState<string | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const canWrite = user?.permissions?.includes(PERMISSIONS.DISPATCH_WRITE) ?? false;
  const status = job.status;
  const isPlanned = status === 'planned';
  const isDispatched = status === 'dispatched';
  // The rule comes from the backend's `refetchable`, the same rule the
  // backend refetch check applies (#2807).
  const canRefetch = job.refetchable;
  const canCloseWriteback = job.writeback_closable;

  // The lifecycle commands stay status-scoped, but the local operator note
  // remains editable for every job state. It never triggers an Onfleet update.
  if (!canWrite) return null;

  const busy = pending !== null;

  const showActionError = (err: unknown) => {
    if (isOnfleetAssignedConflict(err)) {
      toast.error(t('job.actions.error.title'), t('job.actions.error.onfleetTaskAssigned'));
      return;
    }
    const detail = err instanceof ApiError ? err.message : t('job.actions.error.generic');
    toast.error(t('job.actions.error.title'), detail);
  };

  const run = async (actionId: string, path: string, body?: unknown): Promise<boolean> => {
    setPending(actionId);
    try {
      const result = await http.post<JobCommandResult | undefined>(
        `/api/fulfillment-jobs/${encodeURIComponent(job.id)}/${path}`,
        body,
      );
      if (removesJob(result) && leaveDetail) {
        // The job is gone: leave its detail and refresh the rest of the
        // namespace without reading the detail again (#2793, #2802).
        leaveDetail();
        void invalidateAfterLeave(queryClient, queryKeys.dispatchJobs, job.id);
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.dispatchJobs.all() });
      }
      toast.success(t('job.actions.success'));
      return true;
    } catch (err) {
      // Surface the backend's human message so the operator can read the
      // rejection reason (wrong_state / duplicate_date / cas_conflict /
      // Onfleet outbound failure) directly.
      showActionError(err);
      return false;
    } finally {
      setPending(null);
    }
  };

  const handleScheduleNow = async () => {
    const yes = await confirm({
      title: t('job.actions.confirm.schedule.title'),
      message: t('job.actions.confirm.schedule.message'),
      type: 'default',
      confirmTestId: 'job-action-confirm',
    });
    if (!yes) return;
    await run('schedule', 'schedule-manually');
  };

  const handleCloseWriteback = async () => {
    const yes = await confirm({
      title: t('job.actions.confirm.closeWriteback.title'),
      message: t('job.actions.confirm.closeWriteback.message'),
      type: 'danger',
      confirmTestId: 'job-action-confirm',
    });
    if (!yes) return;
    await run('close-writeback', 'close-writeback');
  };

  const handleCancel = async () => {
    const yes = await confirm({
      title: t('job.actions.confirm.cancel.title'),
      message: t('job.actions.confirm.cancel.message'),
      type: 'danger',
      confirmTestId: 'job-action-confirm',
    });
    if (!yes) return;
    await run('cancel', 'cancel');
  };

  const handleCancelDispatched = async () => {
    const yes = await confirm({
      title: t('job.actions.confirm.cancelDispatched.title'),
      message: t('job.actions.confirm.cancelDispatched.message'),
      type: 'danger',
      confirmTestId: 'job-action-confirm',
    });
    if (!yes) return;
    await run('cancel-dispatched', 'cancel-dispatched');
  };

  const openReschedule = () => {
    setRescheduleDate(job.delivery_date ?? '');
    setRescheduleError(null);
    setRescheduleOpen(true);
  };

  const submitReschedule = async () => {
    const date = rescheduleDate.trim();
    if (!date) {
      setRescheduleError(t('job.actions.reschedule.dateRequired'));
      return;
    }
    const ok = await run('reschedule', 'reschedule', { shippingDate: date });
    if (ok) setRescheduleOpen(false);
  };

  const openOperatorNote = () => {
    setNoteDraft(job.operator_note ?? '');
    setNoteOpen(true);
  };

  const submitOperatorNote = async () => {
    const note = noteDraft.trim() || null;
    setPending('operator-note');
    try {
      await http.patch(`/api/fulfillment-jobs/${encodeURIComponent(job.id)}/operator-note`, {
        note,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.dispatchJobs.all() });
      toast.success(t('job.actions.note.success'));
      setNoteOpen(false);
    } catch (err) {
      showActionError(err);
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <div data-testid="job-actions-toolbar" className="flex flex-wrap items-center gap-2">
        {isPlanned && (
          <>
            <Button
              data-testid="job-action-schedule"
              size="sm"
              onClick={handleScheduleNow}
              disabled={busy}
            >
              <Send className="mr-1.5 h-3.5 w-3.5" />
              {t('job.actions.scheduleNow')}
            </Button>
            <Button
              data-testid="job-action-reschedule"
              size="sm"
              variant="outline"
              onClick={openReschedule}
              disabled={busy}
            >
              <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
              {t('job.actions.reschedule')}
            </Button>
          </>
        )}

        {canRefetch && (
          <Button
            data-testid="job-action-refetch"
            size="sm"
            variant="outline"
            onClick={() => run('refetch', 'refetch')}
            disabled={busy}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {t('job.actions.refetch')}
          </Button>
        )}

        <Button
          data-testid="job-action-edit-note"
          size="sm"
          variant="outline"
          onClick={openOperatorNote}
          disabled={busy}
        >
          <StickyNote className="mr-1.5 h-3.5 w-3.5" />
          {t('job.actions.note.edit')}
        </Button>

        {(isPlanned || isDispatched || canCloseWriteback) && (
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="job-actions-more"
              aria-label={t('job.actions.more')}
              disabled={busy}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-surface-2 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <MoreHorizontal className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {isPlanned && (
                <DropdownMenuItem
                  data-testid="job-action-cancel"
                  variant="destructive"
                  disabled={busy}
                  onClick={handleCancel}
                >
                  <Ban className="h-3.5 w-3.5" />
                  {t('job.actions.cancel')}
                </DropdownMenuItem>
              )}
              {isDispatched && (
                <DropdownMenuItem
                  data-testid="job-action-cancel-dispatched"
                  variant="destructive"
                  disabled={busy}
                  onClick={handleCancelDispatched}
                >
                  <XCircle className="h-3.5 w-3.5" />
                  {t('job.actions.cancelDispatched')}
                </DropdownMenuItem>
              )}
              {canCloseWriteback && (
                <DropdownMenuItem
                  data-testid="job-action-close-writeback"
                  variant="destructive"
                  disabled={busy}
                  onClick={handleCloseWriteback}
                >
                  <BellOff className="h-3.5 w-3.5" />
                  {t('job.actions.closeWriteback')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <Modal
        isOpen={rescheduleOpen}
        onClose={busy ? () => {} : () => setRescheduleOpen(false)}
        size="sm"
      >
        <ModalHeader onClose={busy ? undefined : () => setRescheduleOpen(false)}>
          <ModalTitle>{t('job.actions.reschedule.dialogTitle')}</ModalTitle>
          <ModalDescription>{t('job.actions.reschedule.dialogSubtitle')}</ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-2">
            <Label htmlFor="job-reschedule-date" className="text-sm">
              {t('job.actions.reschedule.dateLabel')}
            </Label>
            <DatePicker
              id="job-reschedule-date"
              data-testid="job-action-reschedule-date"
              value={rescheduleDate}
              onChange={(next) => {
                setRescheduleDate(next);
                setRescheduleError(null);
              }}
              disabled={busy}
            />
            {rescheduleError && (
              <p data-testid="job-action-reschedule-error" className="text-sm text-destructive">
                {rescheduleError}
              </p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setRescheduleOpen(false)} disabled={busy}>
            {t('ui.cancel')}
          </Button>
          <Button
            data-testid="job-action-reschedule-submit"
            onClick={submitReschedule}
            disabled={busy}
          >
            {busy ? t('ui.processing') : t('job.actions.reschedule.submit')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={noteOpen} onClose={busy ? () => {} : () => setNoteOpen(false)} size="sm">
        <ModalHeader onClose={busy ? undefined : () => setNoteOpen(false)}>
          <ModalTitle>{t('job.actions.note.dialogTitle')}</ModalTitle>
          <ModalDescription>{t('job.actions.note.dialogSubtitle')}</ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-2">
            <Label htmlFor="job-operator-note" className="text-sm">
              {t('job.actions.note.label')}
            </Label>
            <Textarea
              id="job-operator-note"
              data-testid="job-action-note-textarea"
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              maxLength={2000}
              placeholder={t('job.actions.note.placeholder')}
              disabled={busy}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setNoteOpen(false)} disabled={busy}>
            {t('ui.cancel')}
          </Button>
          <Button data-testid="job-action-note-save" onClick={submitOperatorNote} disabled={busy}>
            {busy ? t('ui.processing') : t('job.actions.note.save')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
